import { useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { createApi, type EnvName } from "./api";
import { cn } from "@/cn";
import { useVoiceControl } from "./VoiceControlContext";
import { useVoiceSession } from "./VoiceSessionContext";
import { useVoiceCommand } from "./useVoiceCommand";
import { usePttVoice } from "./usePttVoice";
import { primeBrowserAudio } from "./voice/browserAudio";
import { pttSidecarConfig } from "./voice/pttSidecarConfig";
import styles from "./voice.module.css";

export function PttVoiceControl({ env }: { env: EnvName }) {
  const api = useMemo(() => createApi(env), [env]);
  const { applyCommands, getUiContext, voiceExecuting } = useVoiceControl();
  const { sessionId, newShift } = useVoiceSession();

  const [open, setOpen] = useState(false);
  const [textInput, setTextInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  const runVoiceCommand = useVoiceCommand({
    api,
    getUiContext,
    applyCommands,
    sessionId,
  });

  const sidecarConfig = pttSidecarConfig();

  const { pttState, turnHistory, error, capture, runTextCommand, clearHistory } = usePttVoice({
    runVoiceCommand,
    sidecarConfig,
  });

  const busy = pttState !== "idle" || voiceExecuting;
  const processing = pttState === "processing" || voiceExecuting;

  const focusTimerRef = useRef<number | null>(null);

  const openPanel = (focusInput = true) => {
    setOpen(true);
    if (!focusInput) return;
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    focusTimerRef.current = window.setTimeout(() => {
      focusTimerRef.current = null;
      inputRef.current?.focus();
    }, 50);
  };

  useEffect(() => {
    return () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (pttState !== "idle" || turnHistory.length > 0) setOpen(true);
  }, [pttState, turnHistory.length]);

  const handleSendText = async () => {
    const text = textInput.trim();
    if (!text || busy) return;
    primeBrowserAudio();
    setTextInput("");
    await runTextCommand(text);
  };

  const recordButtonTitle = "Hold to talk (or hold Space)";

  const handleNewShift = () => {
    newShift();
    clearHistory();
    setOpen(false);
  };

  const statusDot =
    pttState === "recording" ? "⏺" : pttState === "processing" ? "⌛" : "●";

  const lastTurn = turnHistory[turnHistory.length - 1];

  return (
    <div ref={widgetRef} className={styles.pttWidget}>
      <div className={styles.pttHeader}>
        <span
          className={cn(
            pttState === "recording"
              ? styles.statusDotRecording
              : pttState === "processing"
                ? styles.statusDotProcessing
                : styles.statusDot,
          )}
          title={pttState}
        >
          {statusDot}
        </span>

        <button
          type="button"
          className={cn(styles.holdBtn, pttState === "recording" && styles.holdBtnRecording)}
          disabled={processing}
          data-testid="ptt-label"
          title={recordButtonTitle}
          onPointerDown={(e: PointerEvent<HTMLButtonElement>) => {
            if (processing || capture.isRecording()) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            primeBrowserAudio();
            openPanel(false);
            void capture.startRecording();
          }}
          onPointerUp={(e: PointerEvent<HTMLButtonElement>) => {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
            capture.stopRecording();
          }}
          onPointerCancel={(e: PointerEvent<HTMLButtonElement>) => {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
            capture.stopRecording();
          }}
        >
          {pttState === "recording" ? "Talking…" : pttState === "processing" ? "…" : "PTT"}
        </button>

        <button
          type="button"
          className={styles.holdBtn}
          data-testid="ptt-toggle-panel"
          onClick={() => (open ? setOpen(false) : openPanel())}
          title="Open PTT command input"
          aria-label="Toggle PTT panel"
        >
          {open ? "▲" : "▼"}
        </button>
      </div>

      {open ? (
        <div className={styles.pttPanel}>
          <div className={styles.textRow}>
            <input
              ref={inputRef}
              type="text"
              className={styles.textInput}
              data-testid="ptt-text-input"
              placeholder="Type voice command…"
              value={textInput}
              disabled={busy}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSendText();
              }}
            />
            <button
              type="button"
              className={styles.textSend}
              data-testid="ptt-text-send"
              disabled={busy || !textInput.trim()}
              onClick={() => void handleSendText()}
            >
              {busy ? "…" : "Send"}
            </button>
          </div>

          {error ? (
            <div className={styles.pttError} data-testid="ptt-error">
              {error}
            </div>
          ) : null}

          {turnHistory.length > 0 ? (
            <div className={styles.turnPanel}>
              {lastTurn ? (
                <div className={styles.turnLast} data-testid="ptt-turn-summary">
                  <div className={styles.turnQ} title={lastTurn.query}>
                    {lastTurn.query}
                  </div>
                  <div
                    className={cn(styles.turnR, !lastTurn.readback && styles.turnRPending)}
                    title={lastTurn.readback ?? undefined}
                    data-testid="ptt-turn-readback"
                  >
                    {lastTurn.readback ?? "…"}
                  </div>
                </div>
              ) : null}
              {turnHistory.length > 1 ? (
                <div className={styles.turnHistory}>
                  {turnHistory
                    .slice(-5, -1)
                    .reverse()
                    .map((t, i) => (
                      <div key={i} className={styles.turn}>
                        <span className={styles.turnQ} title={t.query}>
                          {t.query}
                        </span>
                        <span className={styles.turnR} title={t.readback ?? undefined}>
                          {t.readback ?? "…"}
                        </span>
                      </div>
                    ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className={styles.shiftFooter}>
            <button
              type="button"
              className={styles.newShiftBtn}
              onClick={handleNewShift}
              title="Clear voice history — clears session memory and turn history (voice LLM only)"
            >
              Clear voice history
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
